/**
 * useDelayedClick Hook - Delayed single-click utility
 *
 * Provides a mechanism to delay single-click actions to allow double-click detection.
 * When a user might double-click, the single-click should be delayed so it can be
 * cancelled if a double-click occurs.
 *
 * Features:
 * - Configurable delay
 * - Cancel pending click (for double-click scenarios)
 * - Automatic cleanup on unmount
 *
 * @module hooks/gestures/useDelayedClick
 * @example
 * ```typescript
 * const { scheduleClick, cancelPendingClick } = useDelayedClick({
 *   onClick: () => navigateToTaskDetails(taskId),
 * });
 *
 * // On single click
 * scheduleClick();
 *
 * // On double-click (before timeout fires)
 * cancelPendingClick();
 * openEditMode();
 * ```
 */

import { useRef, useCallback, useEffect } from 'react';
import { INTERACTION_SINGLE_CLICK_DELAY } from '../../constants';
import type { DelayedClickOptions, DelayedClickReturn } from './types';

/**
 * Hook for delayed single-click with cancellation support
 *
 * @param options - Delayed click configuration options
 * @returns Object with scheduleClick and cancelPendingClick functions
 */
export const useDelayedClick = (options: DelayedClickOptions): DelayedClickReturn => {
  const { delay = INTERACTION_SINGLE_CLICK_DELAY, onClick } = options;

  // Timer ref for cleanup
  const timerRef = useRef<number | null>(null);

  /**
   * Cancel any pending click timer
   */
  const cancelPendingClick = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Schedule a delayed click
   * Click will fire after delay unless cancelled
   */
  const scheduleClick = useCallback(() => {
    // Cancel any existing pending click
    cancelPendingClick();

    // Schedule new click
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      onClick();
    }, delay);
  }, [delay, onClick, cancelPendingClick]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      cancelPendingClick();
    };
  }, [cancelPendingClick]);

  return {
    scheduleClick,
    cancelPendingClick,
  };
};
